/**
 * Capacity policy for human-chat (service) desks.
 *
 * Mirrors the orchestrator contract (MOV-16784). A desk has at most one capacity
 * policy plus any number of per-agent overrides. The policy governs how live-chat
 * conversations are distributed across human agents:
 *  - `max_conversations`            — open conversations an agent can hold before routing skips them
 *  - `fair_distribution_limit`      — new assignments an agent can receive within the window (0 = off)
 *  - `fair_distribution_window_seconds` — the window the limit applies to
 *  - `conversation_priority`        — which waiting conversation to pick next
 *
 * When no policy is persisted yet the GET endpoint returns a desk-derived default
 * with `policy_id === null`, signalling the client to POST (create) on first save.
 */

export enum ConversationPriority {
  LONGEST_WAITING = 'longest_waiting',
  EARLIEST_CREATED = 'earliest_created',
}

export type CapacityPolicy = {
  /** `null` in the desk-derived default response (no policy persisted yet) → POST to create. */
  policy_id: string | null;
  desk_id: string;
  name: string;
  max_conversations: number; // 1–500
  fair_distribution_limit: number; // 0–1000 (0 disables fair distribution)
  fair_distribution_window_seconds: number; // 60–86400
  conversation_priority: ConversationPriority;
  enabled: boolean;
  created?: string;
  updated?: string;
};

export type NewCapacityPolicy = Omit<CapacityPolicy, 'policy_id' | 'created' | 'updated'>;

export type AgentCapacityOverride = {
  override_id: string;
  policy_id: string;
  user_id: string;
  /** `null` means "inherit from the policy". */
  max_conversations: number | null;
  fair_distribution_limit: number | null;
};

export type AgentCapacityOverrides = { overrides: AgentCapacityOverride[] };

/** Payload for the per-agent override upsert endpoint. */
export type CapacityOverridePayload = {
  max_conversations?: number | null;
  fair_distribution_limit?: number | null;
};

/** Validation bounds shared between the UI and the orchestrator schemas. */
export const CAPACITY_LIMITS = {
  maxConversations: { min: 1, max: 500 },
  fairDistributionLimit: { min: 0, max: 1000 },
} as const;

/**
 * Discrete time-window options offered in the UI, stored as seconds.
 * Labels are resolved via i18n at the call site.
 */
export const TIME_WINDOW_OPTIONS: { seconds: number; labelKey: string }[] = [
  { seconds: 300, labelKey: 'environments.time_window_5m' },
  { seconds: 900, labelKey: 'environments.time_window_15m' },
  { seconds: 1800, labelKey: 'environments.time_window_30m' },
  { seconds: 3600, labelKey: 'environments.time_window_1h' },
  { seconds: 14400, labelKey: 'environments.time_window_4h' },
  { seconds: 28800, labelKey: 'environments.time_window_8h' },
  { seconds: 86400, labelKey: 'environments.time_window_24h' },
];

export const TIME_WINDOW_SECONDS = TIME_WINDOW_OPTIONS.map((o) => o.seconds);

/**
 * Defaults offered for a brand-new (unsaved) capacity policy, i.e. when the GET endpoint
 * responds with `policy_id: null`. Independent of whatever fallback the orchestrator applies
 * to live routing for policy-less desks — this only shapes what the UI pre-fills before the
 * user saves their first policy.
 */
export const CAPACITY_DEFAULTS = {
  maxConversations: 10,
  fairDistributionLimit: 10,
  fairDistributionWindowSeconds: TIME_WINDOW_OPTIONS[0].seconds,
} as const;
